import { useState, useRef, useEffect, useLayoutEffect } from 'react'
import { useLocation } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { MessageCircle, X, Send } from 'lucide-react'
import {
  CHIROPRACTIC_CHIPS,
  CHIROPRACTIC_OPENING_MESSAGE,
  CHIROPRACTIC_PAGE_CONTEXT,
} from '../../lib/chiropracticChatConfig'

const CHIPS = [
  'See Pricing',
  "What's AI Automation?",
  'Book a Discovery Call',
  'We already have an IT provider',
]

const OPENING_MESSAGE =
  "Hey! I'm the NTC assistant. Are you looking to fix a current tech problem, or exploring what better IT support could look like for your team?"

function getSessionKey(): string {
  if (typeof sessionStorage === 'undefined') return 'ssg-placeholder'
  let key = sessionStorage.getItem('ntc_chat_session')
  if (!key) {
    key = crypto.randomUUID()
    sessionStorage.setItem('ntc_chat_session', key)
  }
  return key
}

interface Message {
  role: 'user' | 'assistant'
  content: string
}

export function ChatWidget() {
  const location = useLocation()
  const isChiroPage = location.pathname === '/chiropractic'
  const openingMessage = isChiroPage ? CHIROPRACTIC_OPENING_MESSAGE : OPENING_MESSAGE
  const chips = isChiroPage ? [...CHIROPRACTIC_CHIPS] : CHIPS

  const [open, setOpen] = useState(false)
  const [messages, setMessages] = useState<Message[]>([{ role: 'assistant', content: OPENING_MESSAGE }])
  const [input, setInput] = useState('')
  const [streaming, setStreaming] = useState(false)
  const [streamContent, setStreamContent] = useState('')
  const [showLeadModal, setShowLeadModal] = useState(false)
  const [leadSubmitted, setLeadSubmitted] = useState(false)
  const [leadName, setLeadName] = useState('')
  const [leadEmail, setLeadEmail] = useState('')
  const [showUnread, setShowUnread] = useState(true)
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const sessionKeyRef = useRef<string | null>(null)

  useEffect(() => {
    sessionKeyRef.current = getSessionKey()
  }, [])

  useLayoutEffect(() => {
    setMessages([{ role: 'assistant', content: openingMessage }])
  }, [openingMessage])

  const scrollToBottom = () => {
    const el = messagesEndRef.current
    if (el && typeof el.scrollIntoView === 'function') {
      el.scrollIntoView({ behavior: 'smooth' })
    }
  }

  useEffect(() => {
    scrollToBottom()
  }, [messages, streamContent])

  useEffect(() => {
    if (open) setShowUnread(false)
  }, [open])

  const sendMessage = async (content: string, email?: string, name?: string) => {
    if (!content.trim() && !email) return
    const userContent = content.trim()
    if (userContent) {
      setMessages((m) => [...m, { role: 'user', content: userContent }])
      setInput('')
    }

    const allMessages: Message[] = userContent
      ? [...messages, { role: 'user', content: userContent }]
      : messages

    const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:8000'
    setStreaming(true)
    setStreamContent('')

    try {
      const res = await fetch(`${apiUrl}/api/chat/stream`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          messages: allMessages.map((m) => ({ role: m.role, content: m.content })),
          session_key: sessionKeyRef.current ?? getSessionKey(),
          email: email || undefined,
          name: name || undefined,
          ...(isChiroPage ? { page_context: CHIROPRACTIC_PAGE_CONTEXT } : {}),
        }),
      })
      if (!res.ok) throw new Error('Stream failed')
      const reader = res.body?.getReader()
      if (!reader) throw new Error('No reader')
      const decoder = new TextDecoder()
      let acc = ''
      while (true) {
        const { done, value } = await reader.read()
        if (done) break
        const chunk = decoder.decode(value, { stream: true })
        const lines = chunk.split('\n')
        for (const line of lines) {
          if (line.startsWith('data: ')) {
            const data = line.slice(6)
            if (data === '[DONE]') {
              setMessages((m) => [...m, { role: 'assistant', content: acc }])
              setStreamContent('')
              break
            }
            acc += data
            setStreamContent(acc)
          }
        }
      }
    } catch (err) {
      console.error('Chat error:', err)
      setMessages((m) => [...m, { role: 'assistant', content: "Sorry, something went wrong. Please try again or call us at 971.238.4801." }])
    } finally {
      setStreaming(false)
    }
  }

  const handleSend = () => {
    if (!input.trim() || streaming) return
    sendMessage(input)
    const totalUserMessages = messages.filter((m) => m.role === 'user').length + 1
    if (totalUserMessages >= 3 && !leadSubmitted) {
      setShowLeadModal(true)
    }
  }

  const handleChip = (chip: string) => {
    sendMessage(chip)
    const totalUserMessages = messages.filter((m) => m.role === 'user').length + 1
    if (totalUserMessages >= 3 && !leadSubmitted) {
      setShowLeadModal(true)
    }
  }

  const handleLeadSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!leadEmail.trim()) return
    sendMessage('', leadEmail, leadName)
    setLeadSubmitted(true)
    setShowLeadModal(false)
  }

  const handleClose = async () => {
    setOpen(false)
    const transcript = messages.map((m) => ({ role: m.role, content: m.content }))
    if (streamContent) {
      transcript.push({ role: 'assistant', content: streamContent })
    }
    const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:8000'
    try {
      await fetch(`${apiUrl}/api/chat/end-session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ session_key: sessionKeyRef.current ?? getSessionKey(), transcript }),
      })
    } catch {
      // ignore
    }
  }

  const userMessageCount = messages.filter((m) => m.role === 'user').length
  const showLeadModalNow = showLeadModal && userMessageCount >= 3 && !leadSubmitted

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className="fixed bottom-6 right-6 z-50 w-14 h-14 rounded-full flex items-center justify-center shadow-lg transition-transform hover:scale-105"
        style={{ backgroundColor: 'var(--accent)' }}
        aria-label="Chat with NTC"
        title="Chat with NTC"
      >
        <MessageCircle className="w-6 h-6 text-white" />
        {showUnread && (
          <span className="absolute -top-1 -right-1 w-3 h-3 rounded-full bg-red-500" />
        )}
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            className="fixed inset-4 md:inset-auto md:bottom-6 md:right-6 md:w-[380px] md:h-[520px] z-50 flex flex-col rounded-xl shadow-2xl overflow-hidden"
            style={{ backgroundColor: 'var(--bg-2)', border: '1px solid var(--accent-line)' }}
          >
            <div className="p-4 border-b flex items-center justify-between" style={{ borderColor: 'var(--accent-line)' }}>
              <div>
                <h3 className="font-display font-bold text-lg">NTC Assistant</h3>
                <p className="text-cream-dim text-xs">Typically replies instantly</p>
              </div>
              <button onClick={handleClose} className="p-2 text-cream-dim hover:text-white">
                <X size={20} />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              {messages.map((m, i) => (
                <div
                  key={i}
                  className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  <div
                    className={`max-w-[85%] px-4 py-2 rounded-lg ${
                      m.role === 'user'
                        ? 'rounded-br-sm'
                        : 'rounded-bl-sm'
                    }`}
                    style={
                      m.role === 'user'
                        ? { backgroundColor: 'var(--accent)', color: 'white' }
                        : { backgroundColor: 'var(--bg-3)', color: 'var(--white)' }
                    }
                  >
                    {m.content}
                  </div>
                </div>
              ))}
              {streaming && streamContent && (
                <div className="flex justify-start">
                  <div
                    className="max-w-[85%] px-4 py-2 rounded-lg rounded-bl-sm"
                    style={{ backgroundColor: 'var(--bg-3)', color: 'var(--white)' }}
                  >
                    {streamContent}
                    <span className="animate-pulse">|</span>
                  </div>
                </div>
              )}
              {streaming && !streamContent && (
                <div className="flex justify-start">
                  <div
                    className="px-4 py-2 rounded-lg rounded-bl-sm flex gap-1"
                    style={{ backgroundColor: 'var(--bg-3)' }}
                  >
                    <span className="w-2 h-2 rounded-full bg-cream-dim animate-bounce" style={{ animationDelay: '0ms' }} />
                    <span className="w-2 h-2 rounded-full bg-cream-dim animate-bounce" style={{ animationDelay: '150ms' }} />
                    <span className="w-2 h-2 rounded-full bg-cream-dim animate-bounce" style={{ animationDelay: '300ms' }} />
                  </div>
                </div>
              )}
              {messages.length <= 1 && (
                <div className="flex flex-wrap gap-2">
                  {chips.map((chip) => (
                    <button
                      key={chip}
                      onClick={() => handleChip(chip)}
                      className="px-3 py-1.5 rounded-full text-sm font-body border transition-colors"
                      style={{ borderColor: 'var(--accent-line)', color: 'var(--accent)' }}
                    >
                      {chip}
                    </button>
                  ))}
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>

            <div className="p-4 border-t flex gap-2" style={{ borderColor: 'var(--accent-line)' }}>
              <input
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSend()}
                placeholder="Type a message..."
                className="flex-1 px-4 py-2 rounded-lg font-body text-white placeholder-cream-faint"
                style={{ backgroundColor: 'var(--bg-3)', border: '1px solid var(--accent-line)' }}
                disabled={streaming}
              />
              <button
                onClick={handleSend}
                disabled={!input.trim() || streaming}
                className="p-2 rounded-lg disabled:opacity-50"
                style={{ backgroundColor: 'var(--accent)' }}
              >
                <Send className="w-5 h-5 text-white" />
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showLeadModalNow && open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 bg-bg/90 flex items-center justify-center p-4 z-10"
            onClick={() => setShowLeadModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.95 }}
              className="w-full max-w-sm p-6 rounded-xl"
              style={{ backgroundColor: 'var(--bg-3)', border: '1px solid var(--accent-line)' }}
              onClick={(e) => e.stopPropagation()}
            >
              <h4 className="font-display font-bold text-lg text-white mb-4">
                Get a summary sent to your inbox
              </h4>
              <form onSubmit={handleLeadSubmit} className="space-y-3">
                <input
                  type="text"
                  placeholder="Name"
                  value={leadName}
                  onChange={(e) => setLeadName(e.target.value)}
                  className="w-full px-4 py-2 rounded-lg font-body"
                  style={{ backgroundColor: 'var(--bg-4)', border: '1px solid var(--accent-line)', color: 'var(--white)' }}
                />
                <input
                  type="email"
                  placeholder="Email"
                  value={leadEmail}
                  onChange={(e) => setLeadEmail(e.target.value)}
                  required
                  className="w-full px-4 py-2 rounded-lg font-body"
                  style={{ backgroundColor: 'var(--bg-4)', border: '1px solid var(--accent-line)', color: 'var(--white)' }}
                />
                <button
                  type="submit"
                  className="w-full py-2 rounded-lg font-body font-medium text-white"
                  style={{ backgroundColor: 'var(--accent)' }}
                >
                  Send me the summary
                </button>
              </form>
              <button
                onClick={() => setShowLeadModal(false)}
                className="mt-3 w-full text-sm text-cream-dim hover:text-white"
              >
                No thanks, keep chatting
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  )
}
